import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

import { ApiError } from "@/api/client";
import { Alert, TextField } from "@/components/ui";
import { useBrand } from "@/lib/branding";
import { browserZone } from "@/lib/time";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "./AuthProvider";

const schema = z.object({
  company_name: z.string().trim().min(1, "Enter your company name"),
  workspace: z.string().regex(/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/, "3-40 lowercase letters, digits or hyphens"),
  full_name: z.string().trim().min(1, "Enter your name"),
  email: z.string().email("Enter a valid email"),
  password: z.string().min(10, "Use at least 10 characters"),
  currency: z.string().length(3),
  accept_terms: z.boolean().refine((v) => v, "Accept the terms to continue"),
});

const CURRENCIES = [
  ["INR", "Indian rupee (₹)"], ["USD", "US dollar ($)"], ["EUR", "Euro (€)"], ["GBP", "British pound (£)"],
  ["AED", "UAE dirham"], ["SGD", "Singapore dollar"], ["AUD", "Australian dollar"],
] as const;
type Values = z.infer<typeof schema>;

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

export function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [requested, setRequested] = useState<{ email: string; workspace: string } | null>(null);
  const { register, handleSubmit, formState, setValue, getFieldState, setError: setFieldError } =
    useForm<Values>({ resolver: zodResolver(schema), defaultValues: { currency: "INR", accept_terms: false } });
  const brand = useBrand();

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      const result = await signup({ ...values, timezone: browserZone() });
      if (result === "pending") setRequested({ email: values.email, workspace: values.workspace });
      else navigate("/", { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        const fields = e.fieldErrors();
        Object.entries(fields).forEach(([k, m]) => setFieldError(k as keyof Values, { message: m }));
        if (e.status === 409) setFieldError("workspace", { message: e.message });
        else if (!Object.keys(fields).length) setError(e.message);
      } else setError("Could not create the workspace. Try again.");
    }
  });

  const companyField = register("company_name");
  if (requested) {
    return (
      <AuthLayout title="Thanks, your request is in" subtitle="We are setting up your workspace.">
        <div className="space-y-4 text-sm">
          <Alert tone="ok">We will email <b>{requested.email}</b> as soon as your workspace is approved, usually within one working day.</Alert>
          <p>Your workspace name is <b>{requested.workspace}</b>. You will use it with your email and password to sign in.</p>
          <p className="text-muted">Questions? Write to <a className="text-brand hover:underline" href={`mailto:${brand.support_email || "info@datadove.tech"}`}>{brand.support_email || "info@datadove.tech"}</a>.</p>
          <Link to="/login" className="btn-ghost w-full">Back to sign in</Link>
        </div>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title="Create your workspace" subtitle="Start with the manufacturing edition. No card required.">
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && <Alert>{error}</Alert>}
        <TextField label="Company name" {...companyField}
          onChange={(e) => {
            void companyField.onChange(e);
            if (!getFieldState("workspace").isDirty) setValue("workspace", slugify(e.target.value));
          }}
          error={formState.errors.company_name?.message} />
        <TextField label="Workspace" hint="Used to sign in, e.g. acme" {...register("workspace")} error={formState.errors.workspace?.message} />
        <TextField label="Your name" autoComplete="name" {...register("full_name")} error={formState.errors.full_name?.message} />
        <TextField label="Work email" type="email" autoComplete="email" {...register("email")} error={formState.errors.email?.message} />
        <TextField label="Password" type="password" autoComplete="new-password" hint="At least 10 characters" {...register("password")} error={formState.errors.password?.message} />
        <div>
          <label htmlFor="currency" className="label">Currency</label>
          <select id="currency" className="input" {...register("currency")}>
            {CURRENCIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        </div>
        <div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-brand" {...register("accept_terms")} />
            <span>I accept the <Link to="/legal/terms" target="_blank" className="text-brand hover:underline">terms of service</Link>,{" "}
              the <Link to="/legal/privacy" target="_blank" className="text-brand hover:underline">privacy policy</Link> and the{" "}
              <Link to="/legal/dpa" target="_blank" className="text-brand hover:underline">data processing agreement</Link> for my company.</span>
          </label>
          {formState.errors.accept_terms && <p className="field-error">{formState.errors.accept_terms.message}</p>}
        </div>
        <button type="submit" className="btn-primary w-full" disabled={formState.isSubmitting}>
          {formState.isSubmitting ? "Creating…" : "Create workspace"}
        </button>
        <p className="text-center text-sm text-muted">
          Already have a workspace? <Link to="/login" className="font-medium text-brand hover:underline">Sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
